import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, RotateCcw, Search, UserRoundX } from "lucide-react";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import accountDeletionService, { DeletedUserSummary } from "../../services/accountDeletionService";

function DeletedUsersList(): React.ReactElement {
  const toast = useToast();
  const [users, setUsers] = useState<DeletedUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [reload, setReload] = useState(0);
  const [target, setTarget] = useState<DeletedUserSummary | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const requestRef = useRef(0);
  const restoreRequestRef = useRef(0);
  const submittingRef = useRef(false);
  const searchId = React.useId();

  useEffect(() => () => { ++restoreRequestRef.current; }, []);
  useEffect(() => {
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    accountDeletionService.listDeletedUsers(controller.signal).then(result => {
      if (requestId === requestRef.current) setUsers(result);
    }).catch(() => {
      if (requestId === requestRef.current) setError("Could not load deleted users. Please refresh to try again.");
    }).finally(() => {
      if (requestId === requestRef.current) setLoading(false);
    });
    return () => { ++requestRef.current; controller.abort(); };
  }, [reload]);

  const restore = async (): Promise<void> => {
    if (!target || submittingRef.current) return;
    const selected = target;
    const requestId = ++restoreRequestRef.current;
    submittingRef.current = true;
    setRestoring(true);
    setRestoreError(null);
    try {
      await accountDeletionService.restoreUser(selected.id);
      if (requestId !== restoreRequestRef.current) return;
      ++requestRef.current;
      setUsers(previous => previous.filter(user => user.id !== selected.id));
      setTarget(null);
      setReload(value => value + 1);
      toast.success("Account restored. The user can sign in again.");
    } catch (cause) {
      if (requestId !== restoreRequestRef.current) return;
      setRestoreError(cause instanceof Error ? cause.message : "Could not restore this account. Please try again.");
    } finally {
      if (requestId === restoreRequestRef.current) {
        submittingRef.current = false;
        setRestoring(false);
      }
    }
  };

  const visibleUsers = users.filter(user => [user.id, user.email, user.name ?? ""].some(value => value.toLowerCase().includes(query.trim().toLowerCase())));
  return <>
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-v7-ink-soft">Restore an account so its owner can sign in again.</p>
      <button type="button" onClick={() => setReload(value => value + 1)} disabled={restoring} className="inline-flex items-center gap-2 rounded-v7-panel border border-v7-ink/15 bg-v7-white px-3 py-2 text-sm font-medium text-v7-ink-soft hover:bg-v7-paper disabled:opacity-50"><RefreshCw className="h-4 w-4" />Refresh</button>
    </div>
    <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white">
      <div className="border-b border-v7-ink/15 p-4">
        <label htmlFor={searchId} className="sr-only">Search deleted users</label>
        <div className="relative max-w-lg"><Search className="absolute left-3 top-3 h-4 w-4 text-v7-ink/60" /><input id={searchId} type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search by name, email, or user ID" className="w-full rounded-v7-panel border border-v7-ink/15 bg-v7-paper py-2 pl-10 pr-3 text-sm text-v7-ink focus:outline-none focus:ring-2 focus:ring-v7-bronze-dark" /></div>
      </div>
      {error ? <p role="alert" className="p-6 text-sm text-v7-rust">{error}</p>
        : loading ? <p role="status" className="p-8 text-sm text-v7-ink/60">Loading deleted users...</p>
          : visibleUsers.length === 0 ? <div className="px-4 py-16 text-center text-v7-ink/60"><UserRoundX className="mx-auto mb-3 h-8 w-8" /><p>{query.trim() ? "No deleted users match your search." : "No deleted users."}</p></div>
            : <div className="overflow-x-auto"><table className="w-full text-left text-sm">
              <thead className="bg-v7-paper text-xs uppercase tracking-wide text-v7-ink/60"><tr>{["Name", "Email", "User ID", "Action"].map(label => <th key={label} scope="col" className="px-4 py-3 font-semibold">{label}</th>)}</tr></thead>
              <tbody className="divide-y divide-v7-ink/15">{visibleUsers.map(user => <tr key={user.id} className="text-v7-ink-soft">
                <td className="px-4 py-4">{user.name || "-"}</td><td className="px-4 py-4">{user.email}</td><td className="px-4 py-4 font-mono text-xs text-v7-ink/60">{user.id}</td>
                <td className="px-4 py-4"><button type="button" aria-label={`Restore ${user.email}`} disabled={restoring} onClick={() => { setTarget(user); setRestoreError(null); }} className="inline-flex items-center gap-2 rounded-v7-panel bg-v7-ink px-3 py-2 font-semibold text-v7-white hover:bg-v7-bronze-dark disabled:opacity-50"><RotateCcw className="h-4 w-4" />Restore</button></td>
              </tr>)}</tbody>
            </table></div>}
    </div>
    <ConfirmationModal appearance="alpha" isOpen={target !== null} onClose={() => setTarget(null)} onConfirm={restore} title="Restore account" confirmText="Restore account" loading={restoring} loadingText="Restoring..."
      message={<div className="space-y-3"><p>Restore <strong className="break-all">{target?.email}</strong>? The user can sign in again. Previous sessions will remain signed out.</p>{restoreError && <p role="alert" className="text-v7-rust">{restoreError}</p>}</div>} />
  </>;
}

export default function DeletedUsersTab(): React.ReactElement {
  const { user } = useAuth();
  return <DeletedUsersList key={user?.id} />;
}
